import React, {useEffect, useMemo, useState} from "react";
import s from "./CreateTrialAccountPage.module.scss";
import ExternalLink from "../common/ExternalLink";
import Button from "../common/Button";
import "../../styles/common/button.scss";
import {useAppSelector} from "../../redux/hooks";
import {deviceStore} from "../../redux/device/deviceStore";
import {useNavigate} from "react-router-dom";
import {appPagePaths} from "../../config/config";
import {useMicrophone} from "../../hooks/microphone.hook";
import {subscribeToReceiveMessagesFromNativeApp} from "../../services/eventBus/nativeApp/receiveMessageFromNativeAppEventBus";

// Import foreground image set
import PngSm from "./images/hands-on-keyboard-with-video-lesson-sm.png";
import PngLg from "./images/hands-on-keyboard-with-video-lesson-lg.png";
import WebpSm from "./images/hands-on-keyboard-with-video-lesson-sm.webp";
import WebpLg from "./images/hands-on-keyboard-with-video-lesson-lg.webp";
import AvifSm from "./images/hands-on-keyboard-with-video-lesson-sm.avif";
import AvifLg from "./images/hands-on-keyboard-with-video-lesson-lg.avif";
import {baseUrlForWebsite} from "../../client/PlaygroundSettings";
import {logger} from "../../services/log/logger";
import {inAppAnalyticsService} from "../../services/inAppAnalyticsService";
import {buildSdtTrialUrl} from "./buildSdtTrialUrl";

const screenClass = 'onboarding/create-trial-account';

export default function CreateTrialAccountPage() {
  const navigate = useNavigate();
  const microphone = useMicrophone();
  const isMicrophoneAvailable: boolean = microphone.isAvailable;
  const redirectTo = isMicrophoneAvailable
    ? appPagePaths.onboarding_setupFeedback_setupFeedbackAndScoring
    : appPagePaths.setupYourKeyboardPage;
  // iPhones in the macOS Simulator app match the isDeviceIpad condition, but the actual UA string does not include "Mac"
  const isDeviceIOS = useAppSelector(deviceStore.getDeviceOsIsIOS);
  const isDeviceOsAndroid = useAppSelector(deviceStore.getDeviceOsIsAndroid);
  const isDeviceMacDesktop = useAppSelector(deviceStore.getDeviceIsMacDesktop);
  const isDeviceWindowsDesktop = useAppSelector(deviceStore.getDeviceIsWindowsDesktop);
  const isDeviceTablet = useAppSelector(deviceStore.getDeviceIsTablet);
  const isDeviceIPad = isDeviceIOS && isDeviceTablet;
  const isDeviceIPhone = isDeviceIOS && !isDeviceTablet;
  const isDeviceAndroidTablet = isDeviceOsAndroid && isDeviceTablet;
  const isDeviceAndroidPhone = isDeviceOsAndroid && !isDeviceTablet;
  const baseTrialUrl: string = `${baseUrlForWebsite}/seven-day-free-trial`;
  let baseFullTrialUrl: string;
  let isUnrecognizedDevice = false;
  const [appInstanceId, setAppInstanceId] = useState<string | null>(null);
  const [appSessionId, setAppSessionId] = useState<string | null>(null);
  const [isTrialUrlLoading, setIsTrialUrlLoading] = useState(true);

  logger.onboarding.info({
    message: `CreateTrialAccountPage rendered`,
    showDataOnOneLine: false,
    data: [
      { key: "isMicrophoneAvailable", value: isMicrophoneAvailable },
      { key: "isDeviceIPad", value: isDeviceIPad },
      { key: "isDeviceIPhone", value: isDeviceIPhone },
      { key: "isDeviceOsAndroid", value: isDeviceOsAndroid },
      { key: "isDeviceMacDesktop", value: isDeviceMacDesktop },
      { key: "isDeviceWindowsDesktop", value: isDeviceWindowsDesktop },
      { key: "isDeviceTablet", value: isDeviceTablet },
      { key: "isDeviceAndroidTablet", value: isDeviceAndroidTablet },
      { key: "isDeviceAndroidPhone", value: isDeviceAndroidPhone },
    ]
  });

  if (isDeviceIPad) {
    baseFullTrialUrl = `${baseTrialUrl}/ipad-seven-day-trial`;
  } else if (isDeviceIPhone) {
    baseFullTrialUrl = `${baseTrialUrl}/iphone-seven-day-trial`;
  } else if (isDeviceMacDesktop) {
    baseFullTrialUrl = `${baseTrialUrl}/macos-seven-day-trial`;
  } else if (isDeviceWindowsDesktop) {
    baseFullTrialUrl = `${baseTrialUrl}/windows-seven-day-trial`;
  } else if (isDeviceAndroidPhone) {
    baseFullTrialUrl = `${baseTrialUrl}/android-phone-seven-day-trial`;
  } else if (isDeviceAndroidTablet) {
    baseFullTrialUrl = `${baseTrialUrl}/android-tablet-seven-day-trial`;
  } else {
    baseFullTrialUrl = baseTrialUrl;
    isUnrecognizedDevice = true;
  }

  const fullTrialUrl = useMemo(
    () => buildSdtTrialUrl(baseFullTrialUrl, isUnrecognizedDevice, appInstanceId, appSessionId),
    [baseFullTrialUrl, isUnrecognizedDevice, appInstanceId, appSessionId],
  );

  // Fetch app attribution IDs and build the complete URL.
  // Recognized devices rely on the device-specific path; the Website redirect adds the App signal.
  // For an unrecognized device there is no device path to redirect, so the App signal (from_app=true)
  // is attached here directly, without os/form_factor, so the sign-up still attributes to the App.
  // Re-builds when native broadcasts appInstanceId_available / appSessionId_available after consent.
  useEffect(() => {
    const fetchAppAttributionIds = async () => {
      try {
        const [instanceId, sessionId] = await Promise.all([
          inAppAnalyticsService.getAppInstanceID(),
          inAppAnalyticsService.getAppSessionID(),
        ]);
        setAppInstanceId(instanceId);
        setAppSessionId(sessionId);
      } finally {
        setIsTrialUrlLoading(false);
      }
    };

    void fetchAppAttributionIds();

    const unsubscribe = subscribeToReceiveMessagesFromNativeApp((message) => {
      if (message.type === 'appInstanceId_available') {
        setAppInstanceId(message.appInstanceId);
      } else if (message.type === 'appSessionId_available') {
        setAppSessionId(message.appSessionId);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <div className={s.createTrialAccountPage}>
      <picture>
        <source media={"(orientation: portrait) and (max-width: 500px)"} width={302} height={254} srcSet={AvifSm} type={"image/avif"} />
        <source media={"(orientation: portrait) and (max-width: 500px)"} width={302} height={254} srcSet={WebpSm} type={"image/webp"} />
        <source media={"(orientation: portrait) and (max-width: 500px)"} width={302} height={254} srcSet={PngSm} type={"image/png"} />
        <source media={"(orientation: landscape) and (min-width: 501px)"} width={568} height={478} srcSet={AvifLg} type={"image/avif"} />
        <source media={"(orientation: landscape) and (min-width: 501px)"} width={568} height={478} srcSet={WebpLg} type={"image/webp"} />
        <source media={"(orientation: landscape) and (min-width: 501px)"} width={568} height={478} srcSet={PngLg} type={"image/png"} />
        <img src={PngLg} alt="Hands on keyboard with tablet showing a video lesson and accuracy score" width={568} height={478} className={"image"} />
      </picture>
      <main>
        <header>
          <h1>Learn piano with your favorite songs, world-class teachers, and real-time feedback</h1>
          <p>The most effective — and most fun — way to learn. Start your journey today!</p>
        </header>
        <div className={s.pageActions}>
          <ExternalLink
            url={fullTrialUrl}
            title={'Create Free Account'}
            additionalClassName={'button primary emphasized'}
            disabled={isTrialUrlLoading}
            onClick={() => {
              inAppAnalyticsService.onButton_press('create-account-via-website', {screenClass});
              inAppAnalyticsService.onCreateAccount_tap({screenClass, destination: 'website'});
            }}
          />
          <Button
            onClick={() => {
              inAppAnalyticsService.onButton_press('create-trial-account-sign-in', {screenClass});
              inAppAnalyticsService.onSignIn_tap({screenClass});
              navigate(`${appPagePaths.signIn}?route-to-page-after-sign-in=${redirectTo}`);
            }}
            text={'Sign In'}
          />
        </div>
      </main>
    </div>
  )
}
